/* eslint-disable max-len */
import axios from "axios";
import { getAuthItem, setAuthItem, getAuthItem as getStoredItem, removeAuthItem } from "../CommonMethods/authSession";

// Storage key for IP address
const IP_ADDRESS_KEY = 'session_ip_address';

/**
 * Get client IP address from external service
 * Note: Backend handles IP validation during login automatically
 * This is only for storing IP locally for reference
 */
export const getClientIP = async () => {
    try {
        // Use external service to get IP
        // Backend extracts IP from request headers during login automatically
        return await getClientIPFromExternal();
    } catch (error) {
        console.error('Error getting client IP:', error);
        return null;
    }
};

/**
 * Get IP from external service
 * Note: Backend extracts IP from request headers during login automatically
 * This is used to store IP locally for reference
 */
const getClientIPFromExternal = async () => {
    try {
        const response = await axios.get('https://api.ipify.org?format=json', {
            timeout: 5000, // 5 second timeout
        });
        return response?.data?.ip || null;
    } catch (error) {
        console.error('Error getting IP from external service:', error);
        return null;
    }
};

/**
 * Store IP address in localStorage
 * @param {string} ipAddress - IP address to store
 */
export const storeIPAddress = (ipAddress) => {
    if (!ipAddress) {
        return;
    }
    try {
        setAuthItem('session_ip_address', ipAddress);
        // Also store timestamp for reference
        setAuthItem('session_ip_timestamp', Date.now().toString());
    } catch (error) {
        console.error('Error storing IP address:', error);
    }
};

/**
 * Get stored IP address from localStorage
 * @returns {string|null} - Stored IP address or null
 */
export const getStoredIPAddress = () => {
    try {
        return getStoredItem('session_ip_address');
    } catch (error) {
        console.error('Error getting stored IP address:', error);
        return null;
    }
};

/**
 * Compare two IP addresses
 * @param {string} currentIP - Current IP address
 * @param {string} storedIP - Stored IP address
 * @returns {boolean} - True if IPs match, false otherwise
 */
export const compareIPAddresses = (currentIP, storedIP) => {
    if (!currentIP || !storedIP) {
        return false;
    }
    // Normalize IPs (remove whitespace, convert to lowercase)
    const normalizedCurrent = currentIP.trim().toLowerCase();
    const normalizedStored = storedIP.trim().toLowerCase();

    return normalizedCurrent === normalizedStored;
};

/**
 * Note: IP validation is handled by backend during login automatically
 * Backend compares IP addresses when isLoggedIn: true is returned in login response
 * This function is kept for potential future use but is not currently called
 * 
 * @param {string} currentIP - Current client IP address
 * @returns {Promise<{error: boolean, data?: object, message?: string}>}
 */
export const validateSessionIP = async (currentIP) => {
    const storedIP = getStoredIPAddress();
    if (!storedIP) {
        // No stored IP, this is first time - allow and store
        if (currentIP) {
            storeIPAddress(currentIP);
        }
        return { error: false, data: { valid: true, isFirstTime: true } };
    }

    // If IPs match, no need to validate
    if (compareIPAddresses(currentIP, storedIP)) {
        return { error: false, data: { valid: true, ipMatch: true } };
    }

    // IPs don't match - Backend handles validation during login
    // This function is kept for reference but validation happens server-side
    return {
        error: false,
        data: {
            valid: true, // Backend will handle actual validation
            currentIP: currentIP,
            storedIP: storedIP,
            message: 'IP validation handled by backend during login',
        },
    };
};

/**
 * Clear stored IP address (on logout)
 */
export const clearStoredIPAddress = () => {
    try {
        removeAuthItem('session_ip_address');
        removeAuthItem('session_ip_timestamp');
    } catch (error) {
        console.error('Error clearing stored IP address:', error);
        // Fallback: manual removal
        const local = typeof window !== 'undefined' ? window.localStorage : null;
        const session = typeof window !== 'undefined' ? window.sessionStorage : null;

        if (local) {
            local.removeItem('session_ip_address');
            local.removeItem('session_ip_timestamp');
        }
        if (session) {
            session.removeItem('session_ip_address');
            session.removeItem('session_ip_timestamp');
        }
    }
};